import React from 'react';
import PropTypes from 'prop-types';
import { Form, Modal, Radio, Checkbox, Row, Col } from 'antd';
import moment from 'moment';
import { LocalizationContext } from '../../../hoc/Localization';
import { EditorContext } from '../../../hoc/Editor';
import CustomTime from './CustomTime';
import Input from '../../Input';
import DatePicker from '../../Input/DatePicker';
import InputNumber from '../../Input/InputNumber';
import Select from '../../Select';
import styles from './styles.less';

const ScheduleModal = ({
  index,
  schedule,
  visible,
  setVisible,
  newSchedule,
  setNewSchedule,
  fetchedDaytimes,
  fetchedWeekDays,
  setSelectedSchedule,
}) => {
  const { edited, updateContent } = React.useContext(EditorContext);
  const { t } = React.useContext(LocalizationContext);

  const [customWeekDays, setCustomWeekDays] = React.useState(
    schedule && !!schedule.custom_weekdays,
  );
  const [customDayTime, setCustomDayTime] = React.useState(schedule && !!schedule.custom_daytime);
  const [customDayt, setCustomDayt] = React.useState();
  const [customDaytEnd, setCustomDaytEnd] = React.useState();
  const [customWeekSelected, setCustomWeekSelected] = React.useState(
    schedule && !!schedule.custom_weekdays,
  );

  const [form] = Form.useForm();

  const FormItem = Form.Item;
  const formItemSettings = {
    labelCol: {
      span: 8,
    },
    wrapperCol: {
      span: 16,
    },
  };

  const {
    start_date: startDate,
    end_date: endDate,
    duration_weeks: durationWeeks,
    duration_type: durationType,
    daytimes,
    weekdays,
    custom_weekdays: customWeekdays,
    custom_daytime: customDaytime,
    custom_daytime_end: customDaytimeEnd,
    available_seats: availableSeats,
  } = schedule || {};
  const [allowedEndDate, setAllowedEndDate] = React.useState();

  React.useEffect(() => {
    setAllowedEndDate(newSchedule ? new Date() : startDate || new Date());
  }, [newSchedule]);

  const daytimesOptions = fetchedDaytimes.map(daytime => ({
    label: t(`add.schedule.${daytime.title}`),
    value: daytime.title,
  }));

  React.useEffect(() => {
    if (visible) {
      let fieldValues = {
        start_date: null,
        end_date: null,
        duration_weeks: null,
        duration_type: 'day',
        available_seats: null,
        daytimes: [],
      };

      if (!newSchedule) {
        fieldValues = {
          start_date: moment(startDate).isValid() ? moment(startDate) : null,
          end_date: moment(endDate).isValid() ? moment(endDate) : null,
          duration_weeks: durationWeeks,
          duration_type: durationType,
          available_seats: availableSeats,
        };

        if (customWeekDays) {
          fieldValues.custom_weekdays = customWeekdays;
        } else {
          fieldValues.weekdays =
            weekdays && weekdays.length ? weekdays.map(weekday => weekday.title) : [];
        }

        if (customDayTime) {
          if (customDaytime) {
            fieldValues.custom_daytime = moment(customDaytime);
          }
          if (customDaytimeEnd) {
            fieldValues.custom_daytime_end = moment(customDaytimeEnd);
          }
        } else {
          fieldValues.daytimes =
            daytimes && daytimes.length ? daytimes.map(daytime => daytime.title) : [];
        }
      }

      form.setFieldsValue(fieldValues);
    }
  }, [visible]);

  const collectValues = () => {
    form.validateFields().then(fields => {
      const field = { ...fields };
      const data = JSON.parse(JSON.stringify(field));

      if (!newSchedule) {
        data.id = schedule.id;
      }

      /* eslint-disable */
      if (data.weekdays && data.weekdays.length) {
        for (const weekday of data.weekdays) {
          for (const fetchedWeekDay of fetchedWeekDays) {
            if (weekday === fetchedWeekDay.title) {
              data.weekdays[data.weekdays.indexOf(weekday)] = fetchedWeekDay;
            }
          }
        }
      }

      if (!customWeekSelected) {
        data.custom_weekdays = null;
      }

      if (data.daytimes && data.daytimes.length) {
        for (const daytime of data.daytimes) {
          for (const fetchedDaytime of fetchedDaytimes) {
            if (daytime === fetchedDaytime.title) {
              data.daytimes[data.daytimes.indexOf(daytime)] = fetchedDaytime;
            }
          }
        }
      }

      /* eslint-enable */

      data.start_date = fields && fields.start_date.toISOString();

      if (moment.isMoment(customDayt)) {
        data.custom_daytime = customDayt;
      } else {
        data.custom_daytime = customDaytime;
      }

      if (moment.isMoment(customDaytEnd)) {
        data.custom_daytime_end = customDaytEnd;
      } else {
        data.custom_daytime_end = customDaytimeEnd;
      }

      if (data.daytimes) {
        data.custom_daytime = null;
        data.custom_daytime_end = null;
      }

      const scheduleDate = new Date();
      const scheduleProvidedAt = scheduleDate.toISOString();

      if (newSchedule) {
        if (edited.schedules && edited.schedules.length) {
          updateContent({
            ...edited,
            schedules: [...edited.schedules, data],
            scheduleProvidedAt,
          });
        } else {
          updateContent({ ...edited, schedules: [data], scheduleProvidedAt });
        }
      } else {
        edited.schedules[index] = data;
        updateContent({ ...edited, schedules: [...edited.schedules], scheduleProvidedAt });
      }

      setSelectedSchedule({ ...data });

      setVisible(false);
      setNewSchedule(false);
    });
  };

  const handleCustomTime = values => {
    setCustomDayt(values.allowedStartTime);
    setCustomDaytEnd(values.allowedEndTime);
  };

  return (
    <Modal
      visible={visible}
      onCancel={() => {
        setVisible(false);
        setNewSchedule(false);
      }}
      onOk={collectValues}
    >
      <Form {...formItemSettings} className={styles.form} form={form}>
        <Form.Item
          name={['start_date']}
          label={t('add.schedule.start.date')}
          rules={[{ required: true, message: 'Start date is required.' }]}
        >
          <DatePicker
            placeholder={t('add.schedule.select')}
            onChange={momentDate => setAllowedEndDate(momentDate.toDate())}
          />
        </Form.Item>
        <FormItem label={t('add.schedule.end.date')} name={['end_date']}>
          <DatePicker
            placeholder={t('add.schedule.select')}
            disabledDate={d =>
              d.isBefore(new Date(allowedEndDate) < new Date() ? new Date() : allowedEndDate)}
          />
        </FormItem>
        <FormItem label={t('add.schedule.duration')}>
          <Row>
            <Col span={12}>
              <FormItem name={['duration_weeks']}>
                <InputNumber min={1} max={100} />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem name={['duration_type']}>
                <Select
                  version="default"
                  options={[
                    { id: 'day', value: t('add.schedule.day') },
                    { id: 'week', value: t('add.schedule.week') },
                  ]}
                />
              </FormItem>
            </Col>
            <Radio.Group
              onChange={value => {
                setCustomWeekSelected(value.target.value);
              }}
              className={styles.radioButton}
              buttonStyle="solid"
              value={customWeekSelected}
            >
              <Radio.Button value={false}>{t('add.schedule.weekdays')}</Radio.Button>
              <Radio.Button value>{t('add.schedule.custom.day')}</Radio.Button>
            </Radio.Group>
          </Row>
        </FormItem>
        {customWeekSelected ? (
          <FormItem label={t('add.schedule.weekdays')} name={['custom_weekdays']}>
            <Input version="long" size="default" />
          </FormItem>
        ) : (
            <FormItem label={t('add.schedule.weekdays')} name={['weekdays']}>
              <Select
                version="long"
                mode="multiple"
                onInputKeyDown={e => {
                  e.preventDefault();
                  setCustomWeekDays(false);
                }}
                options={fetchedWeekDays.map(weekday => ({
                  id: weekday.title,
                  value: t(`add.schedule.${weekday.title}`),
                }))}
              />
            </FormItem>
          )}

        <FormItem label={t('add.schedule.daytime')}>
          <Radio.Group
            onChange={value => {
              setCustomDayTime(value.target.value);
            }}
            buttonStyle="solid"
            value={customDayTime}
            className={styles.radioButton}
          >
            <Radio.Button value={false}>{t('add.schedule.daytime')}</Radio.Button>
            <Radio.Button value>{t('add.schedule.custom.time')}</Radio.Button>
          </Radio.Group>
          {customDayTime ? (
            <FormItem name={['custom_daytime']}>
              <CustomTime
                initialValues={{ customDaytime, customDaytimeEnd }}
                handleCustomTime={handleCustomTime}
                placeholder={t('add.schedule.select')}
              />
            </FormItem>
          ) : (
              <FormItem name={['daytimes']}>
                <Checkbox.Group options={daytimesOptions} />
              </FormItem>
            )}
        </FormItem>
        <FormItem name={['available_seats']} label={t('add.schedule.seats')}>
          <InputNumber min={1} />
        </FormItem>
      </Form>
    </Modal>
  );
};

ScheduleModal.propTypes = {
  form: PropTypes.object,
  visible: PropTypes.bool,
  schedule: PropTypes.object,
  newSchedule: PropTypes.bool,
  fetchedDaytimes: PropTypes.array,
  fetchedWeekDays: PropTypes.array,
  index: PropTypes.number.isRequired,
  setVisible: PropTypes.func.isRequired,
  setNewSchedule: PropTypes.func.isRequired,
  setSelectedSchedule: PropTypes.func.isRequired,
};

ScheduleModal.defaultProps = {
  form: {},
  schedule: {},
  visible: false,
  newSchedule: false,
  fetchedDaytimes: [],
  fetchedWeekDays: [],
};

export default ScheduleModal;
